import { getCookie } from 'tiny-cookie'

import { TixTime } from '@/TixTime/TixTime'
import { API } from '@/api/API'
import { linkTGRelations, linkToMetadata, nestConfigEntities } from '@/api/Helpers'
import type { EventWithConfig, LinkedTG, WithMeta } from '@/api/types/processedEntities'
import { formatCountry } from '@/helpers/CountriesAndRegions'
import { mapEntries } from '@/helpers/DictHelpers'
import { timezone } from '@/helpers/Environment'
import { groupBy, indexItemsById } from '@/helpers/IndexHelpers'
import type { DeprecatedMembership, MembershipWithEntity } from '@/state/Membership'

interface MemberStoreState {
  identityApiResponse: IdentityApiResponse
  apiResponse: MemberApiResponse
  config: ConfigApiResponse
}

type IdentityApiResponse = ApiResponse<'identity' | 'meta' | 'user'>
type ConfigApiResponse = ApiResponse<'config'>
type MemberApiResponse = ApiResponse<
  | 'membership'
  | 'ticket_order'
  | 'ticket'
  | 'ticket_group'
  | 'ticket_type'
  | 'event_template'
  | 'membership_program'
  | 'role'
>

interface Getters {
  allTickets: Ticket[]
  autoRenewal: AutoRenewState
  eventTemplateWithConfig: EventWithConfig
  eventTemplatesWithConfig: EventWithConfig[]
  identity: Identity | null
  identityWithMeta: WithMeta<ApiEntity>[]
  memberDetailsByType: Dict<Ticket[]>
  membership?: DeprecatedMembership
  membershipExpiry?: TixTime
  membershipId: string
  membershipLevelName: string
  membershipScanCode: string
  memberships: MembershipWithEntity[]
  membershipsWithSummaries: DeprecatedMembership[]
  oldestMembership?: DeprecatedMembership
  ticketGroupWithConfig?: LinkedTG
  ticketGroupsWithConfig: LinkedTG[]
  ticketTypes: TicketType[]
  tickets: Ticket[]
  user: UserEntity | null
}

const emptyApiResource = {
  _data: [],
  _count: 0,
}

function defaultState(): MemberStoreState {
  return {
    identityApiResponse: {
      identity: emptyApiResource,
      meta: emptyApiResource,
      user: emptyApiResource,
    },
    apiResponse: {
      membership: emptyApiResource,
      ticket_order: emptyApiResource,
      ticket: emptyApiResource,
      ticket_group: emptyApiResource,
      event_template: emptyApiResource,
      membership_program: emptyApiResource,
      ticket_type: emptyApiResource,
      role: emptyApiResource,
    },
    config: {
      config: emptyApiResource,
    },
  }
}

/**
 * `apiResponse` is the whole API response. It includes all current and future memberships.
 *
 * If there is no current membership it includes the most recent expired membership.
 *
 * Memberships are in valid_to descending order;
 *
 *   - The first one is the furthest in the future.
 *   - The last one is the current (or most recently expired) membership.
 *
 * If there is one membership only, it could be current, expired, or (very unlikely) future.
 */
export default {
  namespaced: true,

  state: defaultState(),

  getters: {
    identity({ identityApiResponse }: MemberStoreState): Getters['identity'] {
      const data = identityApiResponse.identity._data
      return data.length > 0 ? data[0] : null
    },

    membershipScanCode(_, { membership }: Getters): Getters['membershipScanCode'] {
      // Each member has a unique scan code, even if they have multiple memberships/programs.
      // TODO Does this need to support users with no membership?
      return membership!.scan_code
    },

    /**
     * @deprecated This doesn't support multi-memberships
     */
    membershipId(_, { membership }: Getters): Getters['membershipId'] {
      // TODO Does this need to support users with no membership?
      return membership!.id
    },

    /**
     * @deprecated This doesn't support multi-memberships
     */
    membershipLevelName(_, { oldestMembership }: Getters): Getters['membershipLevelName'] {
      // Use the oldest membership to show the current/previous role.
      // TODO Use the highest ranking current/future membership based on role._rank.
      // TODO Does this need to support users with no membership?
      return oldestMembership!.role
    },

    /**
     * @deprecated This doesn't support multi-memberships
     */
    autoRenewal(_, { membership }: Getters): Getters['autoRenewal'] {
      // TODO Does this need to support users with no membership?
      return membership!.auto_renew_state
    },

    memberDetailsByType(_, { tickets }: Getters): Getters['memberDetailsByType'] {
      const byType = groupBy('ticket_type_id', tickets)
      return mapEntries(byType, (tickets) => tickets)
    },

    /**
     * @deprecated This doesn't support multi-memberships
     */
    membershipExpiry(_, { membershipsWithSummaries: memberships }: Getters): Getters['membershipExpiry'] {
      if (memberships.length > 0) {
        return memberships[0].validTo
      }
    },

    // TODO Move next to identity getter.
    identityWithMeta({ identityApiResponse }: MemberStoreState): Getters['identityWithMeta'] {
      return linkToMetadata('identity', identityApiResponse.identity._data, identityApiResponse.meta._data).map((i) => {
        if (i.meta.country) {
          // Members may have invalid country data due to being imported.
          i.meta.country = formatCountry(i.meta.country)!
        }
        return i
      })
    },

    /**
     * Newest membership (furthest in the future). May be a current or expired membership.
     *
     * @deprecated This doesn't support multi-memberships
     */
    membership(_, { membershipsWithSummaries: memberships }: Getters): Getters['membership'] {
      if (memberships.length > 0) {
        return memberships[0]
      }
    },

    /**
     * Current or previous membership.
     *
     * May be a future membership if there are no current or expired memberships.
     *
     * @deprecated This doesn't support multi-memberships
     */
    oldestMembership(
      _: MemberStoreState,
      { membershipsWithSummaries: memberships }: Getters,
    ): Getters['oldestMembership'] {
      if (memberships.length > 0) {
        return memberships[memberships.length - 1]
      }
    },

    /**
     * @deprecated Use the memberships getter & MembershipSummary or MembershipWithEntity types.
     */
    membershipsWithSummaries({ apiResponse }: MemberStoreState): Getters['membershipsWithSummaries'] {
      return apiResponse.membership._data.map((membership) => ({
        // This smells. Don't mix raw entities with processed data.
        ...membership,
        benefits: membership.member_event_purchase_limit,
        validTo: new TixTime(membership.valid_to, timezone),
        validFrom: new TixTime(membership.valid_from, timezone),
      }))
    },

    memberships(
      { apiResponse }: MemberStoreState,
      { ticketGroupsWithConfig, eventTemplatesWithConfig }: Getters,
    ): Getters['memberships'] {
      const programs = indexItemsById(apiResponse.membership_program._data)
      const roles = indexItemsById(apiResponse.role._data)
      const groups = indexItemsById(ticketGroupsWithConfig)
      const events = indexItemsById(eventTemplatesWithConfig)

      interface Item {
        membership: MembershipEntity
        role: MembershipRole
        program?: MembershipProgram
      }

      return apiResponse.membership._data
        .map((membership): Item => {
          return {
            membership,
            role: roles[membership.role_id],
            // Older memberships may not have a membership program.
            program: membership.membership_program_id ? programs[membership.membership_program_id] : undefined,
          }
        })
        .sort((a, b) => {
          // Sort by program rank and then membership role
          // TODO Deduplicate from CMS
          // @see https://github.com/stqry/tix-unicity/blob/7012619/apps/cms-next/src/helpers/customer.ts#L77-L101
          if (a.program && b.program) {
            return a.program._rank - b.program._rank
          } else {
            return a.role._rank - b.role._rank
          }
        })
        .map(({ membership, program }) => {
          return {
            entity: membership,
            name: program?.name,
            benefits: membership.member_event_purchase_limit,
            validTo: new TixTime(membership.valid_to, timezone),
            validFrom: new TixTime(membership.valid_from, timezone),
            ticketGroup: groups[membership.ticket_group_id],
            eventTemplate: events[membership.event_template_id],
          }
        })
    },

    allTickets({ apiResponse }: MemberStoreState): Getters['allTickets'] {
      return apiResponse.ticket._data.filter((ticket) => ticket.state !== 'cancelled')
    },

    /**
     * @deprecated This doesn't support multi-memberships
     */
    tickets({ apiResponse }: MemberStoreState, { membership }: Getters): Getters['tickets'] {
      // Only include tickets from current membership.
      // TODO Does this need to support users with no membership?
      return apiResponse.ticket._data
        .filter((ticket) => ticket.ticket_order_id === membership!.ticket_order_id)
        .filter((ticket) => ticket.state !== 'cancelled')
    },

    ticketTypes({ apiResponse }: MemberStoreState): Getters['ticketTypes'] {
      return apiResponse.ticket_type._data
    },

    user({ identityApiResponse }: MemberStoreState): Getters['user'] {
      const data = identityApiResponse.user._data
      return data.length > 0 ? data[0] : null
    },

    ticketGroupsWithConfig({ apiResponse, config }: MemberStoreState): Getters['ticketGroupsWithConfig'] {
      const linked = linkTGRelations(apiResponse.ticket_group._data, [], apiResponse.ticket_type._data)
      return nestConfigEntities(linked, config.config._data)
    },

    /**
     * @deprecated This doesn't support multi-memberships
     */
    ticketGroupWithConfig(
      _: MemberStoreState,
      { membership, ticketGroupsWithConfig }: Getters,
    ): Getters['ticketGroupWithConfig'] {
      // TODO Does this need to support users with no membership?
      return ticketGroupsWithConfig.find((group) => group.id === membership!.ticket_group_id)
    },

    eventTemplatesWithConfig({ apiResponse, config }: MemberStoreState): Getters['eventTemplatesWithConfig'] {
      return nestConfigEntities(apiResponse.event_template._data, config.config._data) as EventWithConfig[]
    },

    /**
     * @deprecated This doesn't support multi-memberships
     */
    eventTemplateWithConfig(_, { eventTemplatesWithConfig }: Getters): Getters['eventTemplateWithConfig'] {
      return eventTemplatesWithConfig[0]
    },
  },

  mutations: {
    identityApiResponse(state: MemberStoreState, identityApiResponse: IdentityApiResponse) {
      state.identityApiResponse = identityApiResponse
    },

    apiResponse(state: MemberStoreState, apiResponse: MemberApiResponse) {
      state.apiResponse = apiResponse
    },

    config(state: MemberStoreState, config: ConfigApiResponse) {
      state.config = config
    },

    /**
     * Clears legacy VueX properties that are no longer used.
     *
     * This removes orphaned data properties that are no longer used and makes it harder to introduce unexpected errors
     * if we reuse these property names for a different purpose in the future.
     *
     * Unlike currently-supported/used properties, these legacy properties are not cleared on logout (clearAll()).
     *
     * This procedure is called on every new app instantiation.
     *
     * TODO Remove them after they have been in production for 12+ months. This could still miss ticket buyers in this scenario;
     *
     *   1. Login, membership API responses are stored, buy tickets, close all tabs.
     *   2. We deploy new app version that renames properties.
     *      - Meanwhile the ticket buyer does not reopen Ticketure on the same device.
     *   3. We deploy a new app version that remove the code that removes clearLegacy().
     *   4. The ticket buyer reopens Ticketure on the same device, and logs in.
     *      - The legacy properties will still be in localStorage, and will never be removed.
     *
     * June 2025;
     *   - `identity` was renamed to `identityApiResponse`
     *   - `previousMembership` was renamed to `apiResponse`
     *
     * @see https://github.com/stqry/tix-web-client/pull/3006
     */
    clearLegacy(state: MemberStoreState) {
      // TypeScript should not know about legacy property names. Any usage of them is a mistake, except for this one to remove them from localStorage.
      // Use square brackets so that TypeScript does not complain about the property names it does not know about.
      state['identity'] = undefined
      state['previousMembership'] = undefined
    },
  },

  actions: {
    clearAll(context) {
      const state = defaultState()
      for (const key in state) {
        context.commit(key, state[key])
      }
    },

    async clearLegacy(context) {
      context.commit('clearLegacy')
    },

    load({ commit }) {
      // Includes a secondary call for identity meta embed.
      // TODO Are all these embeds still used and necessary?
      // TODO Merge these into one, using embeds?
      const membershipEmbeds = [
        'role',
        'identity_link',
        'ticket',
        'ticket_order',
        'ticket.ticket_type',
        'ticket.event_template',
        'ticket.ticket_group',
        'role.membership_program',
      ]
      const fetches = [
        API.getUncached('my/membership', { embed: membershipEmbeds }),
        API.getUncached(`my/user`, { embed: 'identity, identity.meta' }),
      ]

      return Promise.all(fetches).then(([response, identityApiResponse]) => {
        // Guard against committing member data after the session was invalidated while these
        // requests were in flight.
        //
        // Without this guard:
        // - During cart hand-off while logged in:
        //   - injectCart tries the /my/cart endpoint which 404s (user doesn't own the cart)
        //     - Calls logout to retry as public identity.
        //   - initializeStores() called on app initialization
        //     - Sees cookie still exists
        //     - Calls this action to load member data
        //     - Depending on timing the response might re-populate apiResponse and identityApiResponse after clearAll wiped it
        //   - The setInterval session check in App.vue detects
        //     - isLoggedIn() is true due to initializeStores() populating this store
        //     - hasSessionCookie is false due to the logout in injectCart
        //     - As a result it calls clearAll again and redirects to the homepage.
        if (!getCookie('session_expires')) return

        commit('apiResponse', response)
        commit('identityApiResponse', identityApiResponse)

        // If the user has a membership, fetch config; re-check session because injectCart may have logged out (404 on /my/cart) after our requests were sent, and an unauthenticated config request would 401.
        if (response.ticket._data.length > 0 && getCookie('session_expires')) {
          const ticketGroupIds = response.ticket._data.map((ticket) => ticket.ticket_group_id)
          const eventTemplateIds = response.ticket._data.map((ticket) => ticket.event_template_id)
          const query = {
            'resource_id._in': ticketGroupIds.concat(eventTemplateIds),
          }
          return API.getUncached('config', { query }).then((response) => {
            commit('config', response)
          })
        }
      })
    },

    updateUser({ commit, getters }, data) {
      return API.patch(`my/user/${getters.user.id}`, {
        body: data,
        embed: 'identity, identity.meta',
      }).then((response) => {
        commit('identityApiResponse', response)
      })
    },

    login({ dispatch }, payload) {
      return API.post('login', { body: payload }).then(() => dispatch('load'))
    },
  },
}
